import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, GraduationCap, CheckCircle2 } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { LogoMark, Wordmark } from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ThemeToggle from '../components/ThemeToggle';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

export default function RegisterGuru() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session, profile, refreshProfile } = useAuth();

  const [nama, setNama] = useState('');
  const [emel, setEmel] = useState('');
  const [telefon, setTelefon] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');

  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [picked, setPicked] = useState([]); // [class_id]
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (session && profile) navigate(`/${profile.role}`, { replace: true });
  }, [session, profile, navigate]);

  useEffect(() => {
    (async () => {
      setLoadingClasses(true);
      try {
        const { data, error } = await supabase.rpc('list_classes_public');
        if (!error && Array.isArray(data)) {
          setClasses(data);
        } else {
          setClasses([]);
        }
      } catch {
        setClasses([]);
      } finally {
        setLoadingClasses(false);
      }
    })();
  }, []);

  const toggleClass = (id) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const bySchool = (Array.isArray(classes) ? classes : []).reduce((acc, c) => {
    const school = c?.sekolah || 'Sekolah';
    (acc[school] = acc[school] || []).push(c);
    return acc;
  }, {});

  const submit = async (e) => {
    e.preventDefault();
    if (password !== password2) { toast.error('Kata laluan tidak sepadan.'); return; }
    if (password.length < 6) { toast.error('Kata laluan minimum 6 aksara.'); return; }
    if (picked.length === 0) { toast.error(t('pilihSekurangSatuKelas')); return; }
    setLoading(true);
    try {
      const { data: su, error: e1 } = await supabase.auth.signUp({ email: emel.trim(), password });
      if (e1) throw e1;
      if (!su.session) {
        const { error: e2 } = await supabase.auth.signInWithPassword({ email: emel.trim(), password });
        if (e2) throw new Error('Sila sahkan emel anda sebelum log masuk.');
      }
      const { error: e3 } = await supabase.rpc('register_guru_links', {
        p_nama: nama.trim(), p_telefon: telefon.trim(),
        p_links: picked.map((class_id) => ({ class_id })),
      });
      if (e3) throw new Error('Pendaftaran gagal. Coba lagi.');
      await refreshProfile();
      toast.success(t('daftarBerjayaGuru'));
      navigate('/guru', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Pendaftaran gagal.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-6 h-16 bg-card border-b border-border">
        <Link to="/masuk" className="flex items-center gap-2"><LogoMark size={34} /><Wordmark size="text-lg" /></Link>
        <div className="flex items-center gap-2"><ThemeToggle /><LanguageSwitcher /></div>
      </header>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-3xl font-bold text-foreground">{t('daftarGuru')}</h1>
        <p className="mt-2 text-muted-foreground">{t('daftarGuruDesc')}</p>

        <form onSubmit={submit} className="mt-8 space-y-8" data-testid="register-guru-form">
          {/* Akaun */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <h2 className="text-lg font-bold text-foreground">{t('maklumatAkaun')}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nama">{t('nama')}</Label>
                <Input id="nama" required value={nama} onChange={(e) => setNama(e.target.value)} className="h-12" data-testid="reg-nama" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefon">{t('telefon')}</Label>
                <Input id="telefon" required value={telefon} onChange={(e) => setTelefon(e.target.value)} placeholder="012-3456789" className="h-12" data-testid="reg-telefon" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emel">{t('emel')}</Label>
                <Input id="emel" type="email" required value={emel} onChange={(e) => setEmel(e.target.value)} className="h-12" data-testid="reg-emel" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pw">{t('kataLaluan')}</Label>
                <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" data-testid="reg-password" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="pw2">{t('sahkanKataLaluan')}</Label>
                <Input id="pw2" type="password" required value={password2} onChange={(e) => setPassword2(e.target.value)} className="h-12" data-testid="reg-password2" />
              </div>
            </div>
          </section>

          {/* Pilih kelas */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-4">
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-brand" />{t('pilihKelasDiajar')}
            </h2>
            {loadingClasses ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />{t('memuatkan')}
              </div>
            ) : classes.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('tiadaKelasDitemui')}</p>
            ) : (
              Object.entries(bySchool).map(([sekolah, list]) => (
                <div key={sekolah} className="space-y-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{sekolah}</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {list.map((c) => {
                      const on = picked.includes(c.class_id);
                      return (
                        <button
                          key={c.class_id} type="button" onClick={() => toggleClass(c.class_id)}
                          data-testid={`reg-class-${c.class_id}`}
                          className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all ${
                            on ? 'border-brand bg-brand-light shadow-sm' : 'border-border bg-card hover:border-border'
                          }`}
                        >
                          <span className={`flex h-5 w-5 items-center justify-center rounded-md border shrink-0 ${on ? 'border-brand bg-brand text-white' : 'border-border text-transparent'}`}>
                            <CheckCircle2 className="h-4 w-4" />
                          </span>
                          <span className="font-medium text-foreground text-sm">{c.kelas}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
            {picked.length > 0 && (
              <p className="text-sm text-muted-foreground">{t('kelasDipilih', { n: picked.length })}</p>
            )}
          </section>

          <Button type="submit" disabled={loading} className="h-12 w-full text-base font-semibold bg-brand text-white hover:bg-brand-royal active:scale-[0.98] transition-transform" data-testid="register-guru-submit">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('daftar')}
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            {t('daftarSebagaiOrangtua')}{' '}
            <Link to="/daftar" className="font-semibold text-brand hover:underline" data-testid="to-register-parent">{t('daftarDiSini')}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
